import {
  WSButton,
  WSElement,
  WSPage,
  WSPageToolbar,
  WSText
} from "@wingspanhq/fe-component-library";
import { WorkLogStatus } from "@wingspanhq/wingspan-api-sdk";
import { useEffect, useMemo, useRef } from "react";
import {
  Redirect,
  Route,
  Switch,
  useHistory,
  useLocation
} from "react-router-dom";
import { Tabs } from "../../../components/Tabs";
import { useQueryWorkLogsSummary } from "../../../query/workLog/queries/useQueryWorkLogsSummary";
import { INVOICING_PATH } from "../../Invoicing";
import { FilterableTableWorkLogsPayeeView } from "../components/TableWorkLogsPayeeView/FilterableTableWorkLogsPayeeView";
import { WorkLogPayeeTabFilterStatus } from "../components/TableWorkLogsPayeeView/filters/types";
import { useModalCreateWorkLogPayee } from "../modals/ModalCreateWorkLogPayee";
import {
  routeHelpersWorkLogsPayee,
  routeHelpersWorkLogsPayeeApproved,
  routeHelpersWorkLogsPayeeInProgress,
  routeHelpersWorkLogsPayeeRejected,
  routeHelpersWorkLogsPayeeSubmitted
} from "../paths";
import { useSession } from "@hooks/useSession";

const useOpenCreateWorkLogModalFromUrl = () => {
  const history = useHistory();
  const location = useLocation();
  const modalCreateWorkLog = useModalCreateWorkLogPayee();
  const hasOpenedModalRef = useRef(false);

  useEffect(() => {
    if (hasOpenedModalRef.current) {
      return;
    }

    const params = new URLSearchParams(location.search);
    const payerId = params.get("payerId");
    const engagementId = params.get("engagementId");

    if (payerId && engagementId) {
      hasOpenedModalRef.current = true;

      // Clear query params from URL
      history.replace(location.pathname);

      modalCreateWorkLog.open({
        defaultPayerId: payerId,
        defaultPayerPayeeEngagementId: engagementId
      });
    }
  }, [history, location.pathname, location.search, modalCreateWorkLog]);
};

export const RoutePayeeLogs = () => {
  const history = useHistory();
  const modalCreateWorkLog = useModalCreateWorkLogPayee();
  const { accountId } = useSession();

  useOpenCreateWorkLogModalFromUrl();

  const queryInProgress = useQueryWorkLogsSummary({
    filter: {
      payeeAccountId: { eq: accountId },
      status: { anyOf: [WorkLogStatus.DRAFT, WorkLogStatus.IN_PROGRESS] }
    }
  });

  const querySubmitted = useQueryWorkLogsSummary({
    filter: {
      payeeAccountId: { eq: accountId },
      status: { anyOf: [WorkLogStatus.IN_REVIEW] }
    }
  });

  const queryApproved = useQueryWorkLogsSummary({
    filter: {
      payeeAccountId: { eq: accountId },
      status: { anyOf: [WorkLogStatus.APPROVED] }
    }
  });

  const queryRejected = useQueryWorkLogsSummary({
    filter: {
      payeeAccountId: { eq: accountId },
      status: { anyOf: [WorkLogStatus.REJECTED] }
    }
  });

  const tabs = useMemo(
    () => [
      {
        label: "In progress",
        path: routeHelpersWorkLogsPayeeInProgress.path,
        badge: queryInProgress.data?.listSize
      },
      {
        label: "Submitted",
        path: routeHelpersWorkLogsPayeeSubmitted.path,
        badge: querySubmitted.data?.listSize
      },
      {
        label: "Approved",
        path: routeHelpersWorkLogsPayeeApproved.path,
        badge: queryApproved.data?.listSize
      },
      {
        label: "Rejected",
        path: routeHelpersWorkLogsPayeeRejected.path,
        badge: queryRejected.data?.listSize
      }
    ],
    [
      queryInProgress.data?.listSize,
      querySubmitted.data?.listSize,
      queryApproved.data?.listSize,
      queryRejected.data?.listSize
    ]
  );

  return (
    <WSPage>
      <WSElement mb="2XL">
        <WSPageToolbar
          title="Work logs"
          actions={[
            {
              label: "New work log",
              buttonKind: "Primary",
              onClick: () => modalCreateWorkLog.open()
            }
          ]}
        />
        <WSText.ParagraphSm color="gray500">
          Track your work and submit for client approval. Approved work logs
          will generate invoices on your{" "}
          <WSButton.Link onClick={() => history.push(INVOICING_PATH)}>
            Invoices
          </WSButton.Link>{" "}
          page.
        </WSText.ParagraphSm>
      </WSElement>

      <Tabs mb="L" tabs={tabs} />

      <Switch>
        <Route path={routeHelpersWorkLogsPayeeInProgress.path}>
          <FilterableTableWorkLogsPayeeView
            status={WorkLogPayeeTabFilterStatus.InProgress}
          />
        </Route>
        <Route path={routeHelpersWorkLogsPayeeSubmitted.path}>
          <FilterableTableWorkLogsPayeeView
            status={WorkLogPayeeTabFilterStatus.Submitted}
          />
        </Route>
        <Route path={routeHelpersWorkLogsPayeeApproved.path}>
          <FilterableTableWorkLogsPayeeView
            status={WorkLogPayeeTabFilterStatus.Approved}
          />
        </Route>
        <Route path={routeHelpersWorkLogsPayeeRejected.path}>
          <FilterableTableWorkLogsPayeeView
            status={WorkLogPayeeTabFilterStatus.Rejected}
          />
        </Route>
        <Redirect
          from={routeHelpersWorkLogsPayee.path}
          to={routeHelpersWorkLogsPayeeInProgress.path}
        />
      </Switch>
    </WSPage>
  );
};
